import { api } from '@/api/endpoints';
import { connectivity } from '@/services/connectivity';
import { getDb } from '@/data/db';
import { createLogger } from '@/lib/logger';

const log = createLogger('ackQueue');

const PENDING_KEY = 'ackQueue.pending';

async function readPending(): Promise<string[]> {
  const raw = await getDb().getMeta(PENDING_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

async function writePending(ids: string[]): Promise<void> {
  await getDb().setMeta(PENDING_KEY, JSON.stringify([...new Set(ids)]));
}

export const ackQueueService = {
  /**
   * Record assignment ids that need to be acknowledged to the server
   * (rem_f31_switch flip from 0 -> 1). Triggered after a sync pulls in
   * brand-new assignments.
   */
  async enqueue(ids: string[]): Promise<void> {
    if (!ids.length) return;
    const existing = await readPending();
    await writePending([...existing, ...ids]);
    void this.flush();
  },

  async flush(): Promise<void> {
    if (!connectivity.online) return;
    const pending = await readPending();
    if (!pending.length) return;
    try {
      const resp = await api.ackBatch(pending);
      const successful = resp.results.filter((r) => r.success).map((r) => r.assignmentId);
      const failed = resp.results.filter((r) => !r.success);
      const remaining = pending.filter((id) => !successful.includes(id));
      await writePending(remaining);
      if (failed.length) {
        log.warn('ack failures', failed);
      }
    } catch (err) {
      log.warn('ack flush failed', err);
    }
  },
};
